/**
 * Link Preview Card Component
 * Displays a rich preview card for URLs with title, description, and image
 */

import React from 'react';
import type { LinkPreview } from '../services/linkPreviewService';

interface LinkPreviewCardProps {
  preview: LinkPreview;
  onRemove?: () => void;
  compact?: boolean;
  className?: string;
}

export const LinkPreviewCard: React.FC<LinkPreviewCardProps> = ({ 
  preview, 
  onRemove,
  compact = false,
  className = ''
}) => {
  const handleClick = () => {
    window.open(preview.url, '_blank', 'noopener,noreferrer');
  };

  if (compact) {
    // Compact version with optional thumbnail
    return (
      <div 
        className={`flex items-center gap-3 p-2 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors overflow-hidden ${className}`}
        onClick={handleClick}
      >
        {/* Thumbnail or favicon */}
        {preview.image ? (
          <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-gray-200">
            <img 
              src={preview.image} 
              alt="" 
              className="w-full h-full object-cover"
              onError={(e) => { 
                // Fallback to favicon if image fails
                const target = e.target as HTMLImageElement;
                target.src = preview.favicon;
                target.className = "w-6 h-6 m-auto";
              }}
            />
          </div>
        ) : (
          <div className="w-12 h-12 flex-shrink-0 rounded bg-gray-200 flex items-center justify-center">
            <img 
              src={preview.favicon} 
              alt="" 
              className="w-6 h-6"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        )}
        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-800 font-medium truncate">{preview.title || preview.domain}</p>
          <p className="text-xs text-gray-400 truncate">{preview.domain}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow w-full max-w-full ${className}`} style={{ minWidth: 0 }}>
      {/* Remove button */}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute top-2 right-2 z-10 w-6 h-6 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center text-xs transition-colors"
          title="Verwijder preview"
        >
          ✕
        </button>
      )}
      
      <div 
        className="flex flex-col sm:flex-row cursor-pointer w-full"
        onClick={handleClick}
        style={{ minWidth: 0 }}
      >
        {/* Image */}
        {preview.image && (
          <div className="sm:w-40 sm:min-w-[160px] h-32 sm:h-auto bg-gray-100 flex-shrink-0 max-w-full">
            <img 
              src={preview.image} 
              alt={preview.title}
              className="w-full h-full object-cover"
              onError={(e) => { 
                (e.target as HTMLImageElement).parentElement!.style.display = 'none'; 
              }}
            />
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 p-3 min-w-0 overflow-hidden">
          {/* Site info */}
          <div className="flex items-center gap-2 mb-1">
            <img 
              src={preview.favicon} 
              alt="" 
              className="w-4 h-4 flex-shrink-0"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="text-xs text-gray-500 uppercase tracking-wide truncate min-w-0">
              {preview.siteName || preview.domain}
            </span>
          </div>
          
          {/* Title */}
          <h4 className="font-medium text-gray-900 line-clamp-2 mb-1 text-sm sm:text-base break-words">
            {preview.title || preview.domain}
          </h4>
          
          {/* Description */}
          {preview.description && (
            <p className="text-gray-600 text-xs sm:text-sm line-clamp-2 break-words">
              {preview.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Loading skeleton for link preview
 */
export const LinkPreviewSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse">
    <div className="flex flex-col sm:flex-row">
      <div className="sm:w-40 h-32 sm:h-24 bg-gray-200" />
      <div className="flex-1 p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-4 h-4 bg-gray-200 rounded" />
          <div className="w-20 h-3 bg-gray-200 rounded" />
        </div>
        <div className="w-3/4 h-4 bg-gray-200 rounded mb-2" />
        <div className="w-full h-3 bg-gray-200 rounded" />
      </div>
    </div>
  </div>
);

/**
 * Multiple link previews display
 */
interface LinkPreviewListProps {
  previews: LinkPreview[];
  onRemove?: (index: number) => void;
  loading?: boolean;
}

export const LinkPreviewList: React.FC<LinkPreviewListProps> = ({ 
  previews, 
  onRemove,
  loading = false
}) => {
  if (loading) {
    return (
      <div className="space-y-2">
        <LinkPreviewSkeleton />
      </div>
    );
  }

  if (previews.length === 0) return null;

  return (
    <div className="space-y-2">
      {previews.map((preview, index) => (
        <LinkPreviewCard
          key={preview.url}
          preview={preview}
          onRemove={onRemove ? () => onRemove(index) : undefined}
        />
      ))}
    </div>
  );
};

export default LinkPreviewCard;

